<template>
  <div class="compare-plans">
    <h2 class="display-6 text-center pt-5 mb-4 text-white">Zusatzleistungen</h2>

    <!-- Desktop/Tablet View with Accordion -->
    <div class="d-none d-md-block">
      <div class="accordion" id="plansAccordion">
        <div
          v-for="(section, index) in selfActivePlans.sections"
          :key="'desktop-' + section.id"
          class="accordion-item bg-transparent border-0 mb-4"
        >
          <h2 class="accordion-header" :id="'heading' + index">
            <button
              class="accordion-button bg-dark text-white"
              :class="{ collapsed: !isSectionOpen(index) }"
              type="button"
              data-bs-toggle="collapse"
              :data-bs-target="'#collapse' + index"
              :aria-expanded="isSectionOpen(index) ? 'true' : 'false'"
              :aria-controls="'collapse' + index"
              @click="toggleSection(index)"
            >
              {{ section.title }}
            </button>
          </h2>
          <div
            :id="'collapse' + index"
            class="accordion-collapse collapse"
            :class="{ show: isSectionOpen(index) }"
            :aria-labelledby="'heading' + index"
            data-bs-parent="#plansAccordion"
          >
            <div class="accordion-body p-0">
              <div class="table-responsive">
                <table class="table text-white">
                  <thead>
                    <tr>
                      <th
                        v-for="(header, i) in selfActivePlans.headers"
                        :key="i"
                        class="plan-header"
                        :class="{ 'text-start': i < 2, 'text-center': i === 2 }"
                      >
                        {{ header }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="feature in section.features"
                      :key="feature.id"
                      class="plan-row"
                    >
                      <td class="plan-feature-name">
                        {{ feature.name }}
                      </td>
                      <td class="plan-feature-desc">
                        {{ feature.description }}
                      </td>
                      <td class="plan-feature-price text-center">
                        <strong>{{ feature.values[2] }}</strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile View with Accordion -->
    <div class="d-md-none">
      <div class="accordion" id="mobilePlansAccordion">
        <div
          v-for="(section, index) in selfActivePlans.sections"
          :key="'mobile-' + section.id"
          class="accordion-item bg-transparent border-0 mb-3"
        >
          <h2 class="accordion-header" :id="'mobileHeading' + index">
            <button
              class="accordion-button bg-dark text-white"
              :class="{ collapsed: !isSectionOpen(index) }"
              type="button"
              data-bs-toggle="collapse"
              :data-bs-target="'#mobileCollapse' + index"
              :aria-expanded="isSectionOpen(index) ? 'true' : 'false'"
              :aria-controls="'mobileCollapse' + index"
              @click="toggleSection(index)"
            >
              {{ section.title }}
            </button>
          </h2>
          <div
            :id="'mobileCollapse' + index"
            class="accordion-collapse collapse"
            :class="{ show: isSectionOpen(index) }"
            :aria-labelledby="'mobileHeading' + index"
            data-bs-parent="#mobilePlansAccordion"
          >
            <div class="accordion-body p-0">
              <div
                v-for="feature in section.features"
                :key="feature.id"
                class="mobile-plan-card mb-3"
              >
                <div class="card bg-dark border-light">
                  <div class="card-body">
                    <h5 class="card-title text-white mb-3">
                      {{ feature.name }}
                    </h5>
                    <p class="card-text text-white-50 mb-3">
                      {{ feature.description }}
                    </p>
                    <div
                      class="d-flex justify-content-between align-items-center"
                    >
                      <span class="text-white">Kosten:</span>
                      <strong class="text-white">{{
                        feature.values[2]
                      }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { selfActivePlans } from "../data/plans";

const openSections = ref({});

// Initialize all sections as collapsed
onMounted(() => {
  selfActivePlans.sections.forEach((_, index) => {
    openSections.value[index] = index === 0; // Open first section by default
  });
});

const isSectionOpen = (index) => {
  return !!openSections.value[index];
};

const toggleSection = (index) => {
  openSections.value[index] = !openSections.value[index];
};
</script>

<style scoped>
.accordion-button {
  font-weight: 600;
  padding: 1rem 1.25rem;
  font-size: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem !important;
  margin-bottom: 0.5rem;
}

.accordion-button:not(.collapsed) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #fff;
  box-shadow: none;
}

.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(255, 255, 255, 0.25);
}

.accordion-button::after {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
}

.accordion-button:not(.collapsed)::after {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23ffffff'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  transform: rotate(-180deg);
}

.accordion-body {
  padding: 0;
}

/* Table Styles */
.table {
  --bs-table-bg: #000;
  --bs-table-color: #fff;
  --bs-table-striped-bg: #0d0d0d;
  --bs-table-striped-color: #fff;
  --bs-table-hover-bg: #1a1a1a;
  --bs-table-hover-color: #fff;
  border-color: rgba(255, 255, 255, 0.1);
}

.table th,
.table td {
  border-color: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  vertical-align: middle;
  color: #fff !important;
}

.table thead th {
  background-color: #111;
  font-weight: 700;
  color: #fff !important;
}

.plan-header {
  color: #fff !important;
  font-weight: 700 !important;
}

/* Base Styles */
.compare-plans {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

/* Section Titles */
.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
  margin-top: 2rem;
}

/* Table Styles */
.table {
  background-color: transparent;
  margin-bottom: 0;
}

.table th {
  border-top: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: 500;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
}

.table td {
  vertical-align: middle;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 1.25rem 1rem;
}

.plan-header {
  font-weight: 600;
  text-align: center;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.02);
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.plan-feature-name {
  width: 25%;
  font-weight: 500;
}

.plan-feature-desc {
  width: 55%;
}

.plan-feature-price {
  width: 20%;
  font-weight: 600;
}

.plan-row:hover {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

/* Mobile Card Styles */
.mobile-plan-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.mobile-plan-card:hover {
  transform: translateY(-2px);
}

.mobile-plan-card .card {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-plan-card .card:hover {
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

/* Responsive Adjustments */
@media (max-width: 991.98px) {
  .table th,
  .table td {
    padding: 0.85rem;
  }

  .section-title {
    font-size: 1.25rem;
    margin-top: 1.5rem;
  }
}
</style>
